import { useFormikContext } from "formik";
import { useEffect, ReactNode, useRef } from "react";

import { updateModelValues } from "../../Helpers/FormikUpdateFunctions";
import { getParamByName, getSessionStorageKey } from "../../Helpers/WindowHelpers";

interface CaptureExternalModelInterface {
    type: string
}

const CaptureExternalModel = ({type}: CaptureExternalModelInterface):ReactNode => {
    const context = useFormikContext<any>();
    const lastSyncedModel = useRef('');

    const initialize = () => {
        if(sessionStorage.getItem(getSessionStorageKey())) return;
        const selectedBrand = (document.querySelector('#tub-brand') as HTMLSelectElement | null)?.value;
        const selectedModel = (document.querySelector('#tub-model') as HTMLSelectElement | null)?.value;
        const queryBrand = selectedBrand || context.values.brand || getParamByName('brand');
        const queryModel = selectedModel || getParamByName('model');
        if(!queryBrand || !queryModel) return;
        if(!sessionStorage.getItem(`${type}_models`)) return;

        const nextKey = `${queryBrand}::${queryModel}`;
        if(lastSyncedModel.current == nextKey && context.values.model == queryModel) return;

        if(context.values.brand != queryBrand) {
            context.setFieldValue('brand', queryBrand);
        }

        lastSyncedModel.current = nextKey;
        updateModelValues(queryModel, queryBrand, type, context);
    }

    const onChange = (e: Event) => {
        const model = (e.currentTarget as HTMLSelectElement).value;
        const brand = (document.querySelector('#tub-brand') as HTMLSelectElement | null)?.value || context.values.brand;
        if(!brand || !model) return;
        lastSyncedModel.current = `${brand}::${model}`;
        updateModelValues(model, brand, type, context);
    }

    useEffect(() => {
        initialize();
        let currentEl: Element | null = null;
        const handleChange = (e: Event) => {
            onChange(e);
        };

        const attach = () => {
            const el = document.querySelector('#tub-model');
            if (!el || el === currentEl) return;

            if (currentEl) {
                currentEl.removeEventListener("change", handleChange as EventListener);
            }

            currentEl = el;
            el.addEventListener("change", handleChange as EventListener);
        };

        attach();
        initialize();

        const obs = new MutationObserver(() => {
            attach();
            initialize();
        });
        obs.observe(document.documentElement, {
            childList: true,
            subtree: true,
        });

        return () => {
            obs.disconnect();
            if (currentEl) {
                currentEl.removeEventListener("change", handleChange as EventListener);
            }
        };
    }, [context.values.brand]);

    return null;
}

export default CaptureExternalModel;
